import { Radio } from "@base-ui/react/radio";
import { RadioGroup } from "@base-ui/react/radio-group";
import type { LucideIcon } from "lucide-react";
import { cx } from "../../lib/cx";
import { Icon } from "./icon";

/**
 * A pill of mutually exclusive options, on Base UI's RadioGroup.
 *
 * A2's `Segmented` (profile `controls.tsx` and again in `after.tsx`) was a
 * `radiogroup` of buttons with no arrow keys: every option was a tab stop and
 * ←/→ did nothing. RadioGroup is one tab stop, and the arrows move the
 * selection, as a radio group does everywhere else.
 *
 * The selected segment is `thumb`, the token for exactly this, so it stays
 * lifted off `surface-2` in dark.
 */

export interface SegmentedOption<T extends string> {
  icon?: LucideIcon;
  label: string;
  value: T;
}

export interface SegmentedProps<T extends string> {
  className?: string;
  disabled?: boolean;
  /** The group's accessible name. */
  label: string;
  onValueChange: (value: T) => void;
  options: readonly SegmentedOption<T>[];
  value: T;
}

export function Segmented<T extends string>({
  value,
  onValueChange,
  options,
  label,
  disabled,
  className,
}: SegmentedProps<T>) {
  return (
    <RadioGroup<T>
      aria-label={label}
      className={cx(
        "grid auto-cols-fr grid-flow-col gap-1 rounded-full bg-surface-2 p-1",
        className
      )}
      disabled={disabled}
      onValueChange={onValueChange}
      value={value}
    >
      {options.map((option) => (
        <Radio.Root
          className="flex h-12 min-w-0 cursor-pointer select-none items-center justify-center gap-1.5 rounded-full px-2 font-semibold text-base text-ink-2 transition-colors hover:text-ink data-disabled:cursor-not-allowed data-checked:bg-thumb data-checked:text-ink data-disabled:opacity-40 data-checked:shadow"
          key={option.value}
          value={option.value}
        >
          {option.icon ? <Icon icon={option.icon} size={18} /> : null}
          <span className="truncate">{option.label}</span>
        </Radio.Root>
      ))}
    </RadioGroup>
  );
}
